import React, { useState, useEffect, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import { supabase } from '../supabaseClient';
import Footer from '../components/Footer';

// ─── Animations ───────────────────────────────────────────────────────────────
const scanline = keyframes`
  0%   { background-position: 0 0; }
  100% { background-position: 0 100vh; }
`;
const neonPulse = keyframes`
  0%, 100% { text-shadow: 0 0 8px #00d4ff, 0 0 20px #00d4ff; }
  50%       { text-shadow: 0 0 14px #ff00ff, 0 0 30px #ff00ff; }
`;
const barWave = keyframes`
  0%, 100% { transform: scaleY(0.3); }
  50%       { transform: scaleY(1); }
`;
const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to   { opacity: 1; transform: translateY(0); }
`;
const spin = keyframes`to { transform: rotate(360deg); }`;
const borderFlow = keyframes`
  0%   { border-color: rgba(0,212,255,0.4); }
  50%  { border-color: rgba(255,0,255,0.6); }
  100% { border-color: rgba(0,212,255,0.4); }
`;

// ─── Layout ───────────────────────────────────────────────────────────────────
const Page = styled.div`
  min-height: 100vh;
  background: linear-gradient(160deg, #06080f 0%, #0c1220 50%, #09111e 100%);
  color: white;
  font-family: 'Arial', sans-serif;
  position: relative;
  overflow-x: hidden;
`;
const Grid = styled.div`
  position: fixed; top: 0; left: 0; width: 100%; height: 100%;
  background:
    linear-gradient(rgba(0,212,255,0.03) 1px, transparent 1px),
    linear-gradient(90deg, rgba(0,212,255,0.03) 1px, transparent 1px);
  background-size: 24px 24px;
  pointer-events: none; z-index: 0;
`;
const Scan = styled.div`
  position: fixed; top: 0; left: 0; width: 100%; height: 100%;
  background: linear-gradient(to bottom, rgba(0,212,255,0.025) 0%, transparent 50%, rgba(0,212,255,0.025) 100%);
  background-size: 100% 6px;
  animation: ${scanline} 5s linear infinite;
  pointer-events: none; z-index: 0;
`;
const Inner = styled.div`
  position: relative; z-index: 2;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px 80px;
`;

// ─── Header ───────────────────────────────────────────────────────────────────
const PageHeader = styled.div`
  text-align: center;
  padding: 70px 20px 50px;
  h1 {
    font-size: 3rem;
    font-weight: 900;
    letter-spacing: 5px;
    text-transform: uppercase;
    animation: ${neonPulse} 3s ease-in-out infinite;
    color: #00d4ff;
    margin: 0 0 10px;
    @media (max-width: 600px) { font-size: 2rem; }
  }
  p {
    color: rgba(255,255,255,0.45);
    font-size: 0.85rem;
    letter-spacing: 3px;
    text-transform: uppercase;
  }
`;

// ─── Station Tabs ──────────────────────────────────────────────────────────────
const TabRow = styled.div`
  display: flex;
  gap: 10px;
  justify-content: center;
  margin-bottom: 48px;
  flex-wrap: wrap;
`;
const Tab = styled.button`
  padding: 13px 28px;
  border-radius: 40px;
  border: 2px solid ${p => p.$active ? '#00d4ff' : 'rgba(255,255,255,0.1)'};
  background: ${p => p.$active
    ? 'linear-gradient(135deg, rgba(0,212,255,0.22), rgba(255,0,255,0.16))'
    : 'rgba(255,255,255,0.04)'};
  color: ${p => p.$active ? '#00d4ff' : 'rgba(255,255,255,0.5)'};
  font-size: 0.82rem;
  font-weight: 700;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  cursor: pointer;
  transition: all 0.25s ease;
  &:hover { border-color: #00d4ff; color: #00d4ff; transform: translateY(-2px); }
`;

// ─── Main layout ──────────────────────────────────────────────────────────────
const TwoCol = styled.div`
  display: grid;
  grid-template-columns: 300px 1fr;
  gap: 24px;
  align-items: start;
  @media (max-width: 860px) { grid-template-columns: 1fr; }
`;

// ─── Track List ───────────────────────────────────────────────────────────────
const TrackListBox = styled.div`
  background: rgba(6,8,18,0.85);
  border: 1px solid rgba(0,212,255,0.15);
  border-radius: 16px;
  backdrop-filter: blur(14px);
  overflow: hidden;
  animation: ${borderFlow} 4s ease infinite;
`;
const TrackListHead = styled.div`
  padding: 18px 20px 14px;
  border-bottom: 1px solid rgba(0,212,255,0.1);
  span {
    color: #00d4ff;
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 2.5px;
    text-transform: uppercase;
  }
`;
const TrackScroll = styled.div`
  max-height: 520px;
  overflow-y: auto;
  padding: 10px 10px;
  &::-webkit-scrollbar { width: 3px; }
  &::-webkit-scrollbar-thumb { background: rgba(0,212,255,0.3); border-radius: 2px; }
`;
const TrackRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 11px 12px;
  border-radius: 10px;
  cursor: pointer;
  border: 1px solid ${p => p.$active ? 'rgba(0,212,255,0.35)' : 'transparent'};
  background: ${p => p.$active ? 'rgba(0,212,255,0.1)' : 'transparent'};
  transition: all 0.2s ease;
  margin-bottom: 4px;
  animation: ${fadeUp} 0.3s ease forwards;
  &:hover { background: rgba(0,212,255,0.07); transform: translateX(3px); }

  .icon {
    width: 36px; height: 36px; border-radius: 8px; flex-shrink: 0;
    display: flex; align-items: center; justify-content: center;
    font-size: 1rem;
    background: ${p => p.$active
      ? 'linear-gradient(135deg,#00d4ff,#ff00ff)'
      : 'rgba(255,255,255,0.06)'};
  }
  .info { flex: 1; min-width: 0;
    .t { color: ${p => p.$active ? '#00d4ff' : 'rgba(255,255,255,0.8)'};
         font-size: 0.85rem; font-weight: 600;
         white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
    .d { color: rgba(255,255,255,0.35); font-size: 0.73rem; margin-top: 2px;
         white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  }
  .badge {
    font-size: 0.58rem; padding: 2px 7px; border-radius: 4px;
    font-weight: 700; letter-spacing: 0.5px; flex-shrink: 0;
    background: ${p => p.$type === 'video' ? 'rgba(255,0,255,0.15)' : 'rgba(0,212,255,0.12)'};
    color: ${p => p.$type === 'video' ? '#ff00ff' : '#00d4ff'};
    border: 1px solid ${p => p.$type === 'video' ? 'rgba(255,0,255,0.3)' : 'rgba(0,212,255,0.25)'};
  }
`;
const EmptyList = styled.div`
  text-align: center; padding: 50px 20px;
  color: rgba(255,255,255,0.25); font-size: 0.85rem;
  .ico { font-size: 2.5rem; display: block; margin-bottom: 12px; }
`;
const LoadBox = styled.div`
  display: flex; flex-direction: column; align-items: center;
  justify-content: center; padding: 50px 20px; gap: 14px;
  .sp { width: 28px; height: 28px; border: 3px solid rgba(0,212,255,0.2);
        border-top-color: #00d4ff; border-radius: 50%;
        animation: ${spin} 0.8s linear infinite; }
  span { color: rgba(255,255,255,0.35); font-size: 0.8rem; }
`;

// ─── Player Area ──────────────────────────────────────────────────────────────
const PlayerBox = styled.div`
  display: flex; flex-direction: column; gap: 20px;
  animation: ${fadeUp} 0.4s ease;
`;
const NowPlayingCard = styled.div`
  background: rgba(6,8,18,0.88);
  border: 1px solid rgba(0,212,255,0.18);
  border-radius: 16px;
  backdrop-filter: blur(14px);
  overflow: hidden;
  animation: ${borderFlow} 5s ease infinite;
`;
const MediaArea = styled.div`
  width: 100%;
  height: ${p => p.$isVideo ? '380px' : '220px'};
  background: linear-gradient(135deg, #060a14, #0f0620);
  position: relative; overflow: hidden;
  display: flex; align-items: center; justify-content: center;
  video, audio { width: 100%; height: 100%; outline: none; }
`;
const Visualizer = styled.div`
  display: flex; align-items: flex-end; gap: 5px; height: 64px;
`;
const Bar = styled.div`
  width: 5px; border-radius: 3px;
  background: linear-gradient(to top, #00d4ff, #ff00ff);
  transform-origin: bottom;
  animation: ${barWave} ${p => p.$dur}s ease-in-out infinite;
  animation-delay: ${p => p.$del}s;
  opacity: ${p => p.$playing ? 1 : 0.25};
  height: 100%;
`;
const AudioControlsRow = styled.div`
  position: absolute; bottom: 0; left: 0; right: 0; padding: 0 16px 14px;
  audio { width: 100%; }
`;
const PlaceholderBox = styled.div`
  text-align: center; color: rgba(255,255,255,0.18);
  .ico { font-size: 4rem; display: block; margin-bottom: 12px; }
  p { font-size: 0.78rem; letter-spacing: 2px; text-transform: uppercase; }
`;
const MetaRow = styled.div`
  padding: 22px 26px 26px;
  .station-tag { font-size: 0.68rem; letter-spacing: 2px; text-transform: uppercase;
                 color: #ff00ff; margin-bottom: 6px; }
  h2 { color: #fff; font-size: 1.35rem; font-weight: 700; margin: 0 0 6px; }
  .desc { color: rgba(255,255,255,0.45); font-size: 0.88rem; line-height: 1.55; }
`;

// ─── Galaxy Station styled components ────────────────────────────────────────
const GalaxyWrap = styled.div`
  position: relative;
  border-radius: 16px;
  overflow: hidden;
  border: 1px solid rgba(0,212,255,0.2);
  box-shadow: 0 0 40px rgba(0,212,255,0.08), 0 0 80px rgba(120,0,255,0.06);
  animation: ${borderFlow} 5s ease infinite;
  background: #020205;
`;
const GalaxyFrame = styled.iframe`
  width: 100%;
  height: 720px;
  border: none;
  display: block;
  @media (max-width: 768px) { height: 500px; }
`;
const GalaxyLabel = styled.div`
  padding: 14px 22px;
  background: rgba(2,2,5,0.9);
  border-top: 1px solid rgba(0,212,255,0.1);
  display: flex; align-items: center; justify-content: space-between;
  span { color: rgba(255,255,255,0.35); font-size: 0.72rem; letter-spacing: 2px; text-transform: uppercase; }
  a { color: #00d4ff; font-size: 0.72rem; letter-spacing: 1px; text-decoration: none;
      &:hover { text-decoration: underline; } }
`;

// ─── Live Streams config (from Kaspa Galaxy Station) ─────────────────────────
const getLiveStream = () => {
  const m = new Date().getMonth(); // 0-indexed
  if (m >= 2  && m <= 4) return { season: 'Spring', vibe: 'Afrobeat N1 Hits',    station: 'Naija Signal-1',     url: 'https://streamer.radio.co/s919e54210/listen' };
  if (m >= 5  && m <= 7) return { season: 'Summer', vibe: 'Amapiano Dancehall',  station: 'Big FM Dancehall',   url: 'https://stream.bigfm.de/dancehall/mp3-128/radio-browser' };
  return                         { season: 'Autumn', vibe: 'Afrofusion Vibes',   station: 'Flavor Jamz Radio',  url: 'https://stream.flavorjamz.com/stream.mp3' };
};

const ALL_LIVE_STREAMS = [
  { season: 'Spring', vibe: 'Afrobeat N1 Hits',   station: 'Naija Signal-1',     url: 'https://streamer.radio.co/s919e54210/listen' },
  { season: 'Summer', vibe: 'Amapiano Dancehall', station: 'Big FM Dancehall',   url: 'https://stream.bigfm.de/dancehall/mp3-128/radio-browser' },
  { season: 'Autumn', vibe: 'Afrofusion Vibes',   station: 'Flavor Jamz Radio',  url: 'https://stream.flavorjamz.com/stream.mp3' },
];

const SEASON_EMOJI = { Winter: '❄️', Spring: '🌸', Summer: '☀️', Autumn: '🍂' };

const LivePlayerBox = styled.div`
  background: rgba(6,8,18,0.88);
  border: 1px solid rgba(0,212,255,0.18);
  border-radius: 16px;
  backdrop-filter: blur(14px);
  overflow: hidden;
  animation: ${borderFlow} 5s ease infinite;
`;
const LiveHeader = styled.div`
  padding: 20px 26px 16px;
  border-bottom: 1px solid rgba(0,212,255,0.1);
  display: flex; align-items: center; justify-content: space-between;
  h3 { color: #00d4ff; font-size: 1rem; font-weight: 700; letter-spacing: 2px;
       text-transform: uppercase; margin: 0; }
  span { font-size: 0.7rem; color: rgba(255,255,255,0.3); letter-spacing: 1px;
         text-transform: uppercase; }
`;
const LiveGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  padding: 20px;
  @media (max-width: 600px) { grid-template-columns: 1fr; }
`;
const LiveCard = styled.div`
  background: ${p => p.$active
    ? 'linear-gradient(135deg, rgba(0,212,255,0.15), rgba(120,0,255,0.12))'
    : 'rgba(255,255,255,0.03)'};
  border: 1px solid ${p => p.$active ? 'rgba(0,212,255,0.4)' : 'rgba(255,255,255,0.07)'};
  border-radius: 12px;
  padding: 16px;
  cursor: pointer;
  transition: all 0.2s ease;
  &:hover { border-color: rgba(0,212,255,0.3); transform: translateY(-2px); }
  .season { font-size: 1.4rem; margin-bottom: 8px; }
  .vibe { color: ${p => p.$active ? '#00d4ff' : '#fff'};
          font-size: 0.9rem; font-weight: 700; margin-bottom: 4px; }
  .station { color: rgba(255,255,255,0.4); font-size: 0.75rem; }
  .live-badge {
    display: inline-flex; align-items: center; gap: 5px;
    font-size: 0.65rem; font-weight: 700; letter-spacing: 1px;
    color: #ff4444; margin-top: 8px;
    &::before { content: ''; width: 6px; height: 6px; border-radius: 50%;
                background: #ff4444;
                animation: ${barWave} 1s ease-in-out infinite; }
  }
`;
const LiveAudioRow = styled.div`
  padding: 0 20px 20px;
  audio { width: 100%; outline: none;
          &::-webkit-media-controls-panel { background: rgba(0,212,255,0.08); } }
`;
const LiveNowPlaying = styled.div`
  padding: 16px 20px 0;
  display: flex; align-items: center; gap: 12px;
  .bars { display: flex; align-items: flex-end; gap: 3px; height: 28px; }
  .info h4 { color: #00d4ff; font-size: 0.95rem; font-weight: 700; margin: 0 0 2px; }
  .info p  { color: rgba(255,255,255,0.4); font-size: 0.78rem; margin: 0; }
`;

// ─── Stations config ──────────────────────────────────────────────────────────
const STATIONS = [
  { id: 'crypto-crossfire',  label: '🔥 Crypto Cross Fire', color: '#ff6600' },
  { id: 'music',             label: '🎵 Music',             color: '#00d4ff' },
  { id: 'political-news',    label: '📰 Political News',    color: '#ff00ff' },
  { id: 'galaxy-station',    label: '🌌 Galaxy Station',    color: '#7c00ff' },
];

// ─── Component ────────────────────────────────────────────────────────────────
const Radio = () => {
  const [station, setStation]         = useState('music');
  const [tracks,  setTracks]          = useState([]);
  const [current, setCurrent]         = useState(null);
  const [loading, setLoading]         = useState(true);
  const [playing, setPlaying]         = useState(false);
  const [liveStream,  setLiveStream]  = useState(() => getLiveStream());
  const [liveActive,  setLiveActive]  = useState(false);
  const [liveLoading, setLiveLoading] = useState(false);
  const mediaRef   = useRef(null);
  const liveAudRef = useRef(null);

  const stationInfo = STATIONS.find(s => s.id === station);
  const isGalaxy    = station === 'galaxy-station';
  const isMusic     = station === 'music';

  useEffect(() => {
    if (isGalaxy) return;
    let cancelled = false;
    setLoading(true); setCurrent(null); setPlaying(false); setLiveActive(false);
    supabase.from('radio_tracks').select('*')
      .eq('station', station)
      .order('created_at', { ascending: false })
      .then(({ data }) => {
        if (cancelled) return;
        const rows = data || [];
        setTracks(rows);
        if (rows.length) setCurrent(rows[0]);
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [station, isGalaxy]);

  const selectTrack = (track) => {
    setLiveActive(false);
    setCurrent(track); setPlaying(false);
    setTimeout(() => {
      if (mediaRef.current) {
        mediaRef.current.load();
        mediaRef.current.play().catch(() => {});
        setPlaying(true);
      }
    }, 80);
  };

  const selectLiveStream = (stream) => {
    setLiveStream(stream);
    setLiveActive(true);
    setLiveLoading(true);
    setTimeout(() => {
      if (liveAudRef.current) {
        liveAudRef.current.load();
        liveAudRef.current.play().catch(() => {});
      }
      setLiveLoading(false);
    }, 100);
  };

  const isVideo = current?.file_type === 'video';

  return (
    <Page>
      <Grid /><Scan />
      <PageHeader>
        <h1>📻 ModMedia Radio</h1>
        <p>Stream live audio &amp; video from our stations</p>
      </PageHeader>

      <Inner>
        <TabRow>
          {STATIONS.map(s => (
            <Tab key={s.id} $active={station === s.id} onClick={() => setStation(s.id)}>
              {s.label}
            </Tab>
          ))}
        </TabRow>

        {/* ── Galaxy Station ── full iframe embed */}
        {isGalaxy && (
          <GalaxyWrap>
            <GalaxyFrame
              src="https://kaspa-galaxy-station.vercel.app/"
              title="Kaspa Galaxy Station"
              allow="autoplay; encrypted-media"
              loading="lazy"
            />
            <GalaxyLabel>
              <span>🌌 Kaspa Galaxy Station — Seasonal Live Radio + Interactive 3D</span>
              <a href="https://kaspa-galaxy-station.vercel.app/" target="_blank" rel="noopener noreferrer">
                Open full screen ↗
              </a>
            </GalaxyLabel>
          </GalaxyWrap>
        )}

        {/* ── Music — live streams strip + existing tracks ── */}
        {isMusic && (
          <>
            {/* Live stream cards */}
            <LivePlayerBox style={{ marginBottom: 24 }}>
              <LiveHeader>
                <h3>📡 Live Radio Streams</h3>
                <span>Powered by Galaxy Station · {liveActive ? 'LIVE ACTIVE' : 'Click to tune in'}</span>
              </LiveHeader>
              <LiveGrid>
                {ALL_LIVE_STREAMS.map(s => (
                  <LiveCard
                    key={s.season}
                    $active={liveActive && liveStream.season === s.season}
                    onClick={() => selectLiveStream(s)}
                  >
                    <div className="season">{SEASON_EMOJI[s.season]}</div>
                    <div className="vibe">{s.vibe}</div>
                    <div className="station">{s.station}</div>
                    <div className="live-badge">LIVE</div>
                  </LiveCard>
                ))}
              </LiveGrid>
            </LivePlayerBox>

            {/* Tracks + player (same layout as other stations) */}
            <TwoCol>
              <TrackListBox>
                <TrackListHead><span>🎵 Music — Tracks</span></TrackListHead>
                <TrackScroll>
                  {loading ? (
                    <LoadBox><div className="sp"/><span>Loading…</span></LoadBox>
                  ) : tracks.length === 0 ? (
                    <EmptyList>
                      <span className="ico">📭</span>
                      No tracks yet.<br/>Check back soon.
                    </EmptyList>
                  ) : tracks.map(t => (
                    <TrackRow key={t.id} $active={!liveActive && current?.id === t.id}
                      $type={t.file_type} onClick={() => selectTrack(t)}>
                      <div className="icon">{t.file_type === 'video' ? '🎬' : '🎙️'}</div>
                      <div className="info">
                        <div className="t">{t.title}</div>
                        {t.description && <div className="d">{t.description}</div>}
                      </div>
                      <span className="badge" $type={t.file_type}>
                        {t.file_type === 'video' ? 'MP4' : 'MP3'}
                      </span>
                    </TrackRow>
                  ))}
                </TrackScroll>
              </TrackListBox>

              <PlayerBox>
                <NowPlayingCard>
                  <MediaArea $isVideo={!liveActive && current?.file_type === 'video'}>
                    {liveActive ? (
                      <>
                        <LiveNowPlaying style={{ position: 'absolute', top: 16, left: 0, right: 0 }}>
                          <div className="bars">
                            {[0.35,0.6,0.85,0.5,0.7,0.4,0.65].map((d,i) => (
                              <Bar key={i} $dur={d * 1.2 + 0.3} $del={i * 0.1} $playing={!liveLoading} />
                            ))}
                          </div>
                          <div className="info">
                            <h4>{SEASON_EMOJI[liveStream.season]} {liveStream.vibe}</h4>
                            <p>{liveStream.station} · {liveStream.season} Season</p>
                          </div>
                        </LiveNowPlaying>
                        <AudioControlsRow>
                          <audio ref={liveAudRef} key={liveStream.url} src={liveStream.url} controls autoPlay />
                        </AudioControlsRow>
                      </>
                    ) : current ? (
                      current.file_type === 'video' ? (
                        <video ref={mediaRef} src={current.file_url} controls
                          onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)} />
                      ) : (
                        <>
                          <Visualizer>
                            {[0.35,0.55,0.75,0.45,0.65,0.85,0.5,0.4,0.7,0.6,0.5,0.45].map((d,i) => (
                              <Bar key={i} $dur={d * 1.4 + 0.4} $del={i * 0.07} $playing={playing} />
                            ))}
                          </Visualizer>
                          <AudioControlsRow>
                            <audio ref={mediaRef} src={current.file_url} controls
                              onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)} />
                          </AudioControlsRow>
                        </>
                      )
                    ) : (
                      <PlaceholderBox>
                        <span className="ico">🎵</span>
                        <p>Select a track or tune into live radio</p>
                      </PlaceholderBox>
                    )}
                  </MediaArea>
                  <MetaRow>
                    <div className="station-tag">🎵 Music</div>
                    {liveActive ? (
                      <>
                        <h2>{liveStream.vibe}</h2>
                        <p className="desc">{liveStream.station} · 24/7 Live Stream</p>
                      </>
                    ) : current ? (
                      <>
                        <h2>{current.title}</h2>
                        {current.description && <p className="desc">{current.description}</p>}
                      </>
                    ) : (
                      <h2 style={{ color: 'rgba(255,255,255,0.25)' }}>Nothing selected</h2>
                    )}
                  </MetaRow>
                </NowPlayingCard>
              </PlayerBox>
            </TwoCol>
          </>
        )}

        {/* ── Standard track-based stations ── */}
        {!isGalaxy && !isMusic && (
          <TwoCol>
            {/* Track List */}
            <TrackListBox>
              <TrackListHead><span>{stationInfo.label} — Tracks</span></TrackListHead>
              <TrackScroll>
                {loading ? (
                  <LoadBox><div className="sp"/><span>Loading…</span></LoadBox>
                ) : tracks.length === 0 ? (
                  <EmptyList>
                    <span className="ico">📭</span>
                    No tracks yet.<br/>Check back soon.
                  </EmptyList>
                ) : tracks.map(t => (
                  <TrackRow key={t.id} $active={current?.id === t.id}
                    $type={t.file_type} onClick={() => selectTrack(t)}>
                    <div className="icon">{t.file_type === 'video' ? '🎬' : '🎙️'}</div>
                    <div className="info">
                      <div className="t">{t.title}</div>
                      {t.description && <div className="d">{t.description}</div>}
                    </div>
                    <span className="badge" $type={t.file_type}>
                      {t.file_type === 'video' ? 'MP4' : 'MP3'}
                    </span>
                  </TrackRow>
                ))}
              </TrackScroll>
            </TrackListBox>

            {/* Player */}
            <PlayerBox>
              <NowPlayingCard>
                <MediaArea $isVideo={isVideo}>
                  {current ? (
                    isVideo ? (
                      <video ref={mediaRef} src={current.file_url} controls
                        onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)} />
                    ) : (
                      <>
                        <Visualizer>
                          {[0.35,0.55,0.75,0.45,0.65,0.85,0.5,0.4,0.7,0.6,0.5,0.45].map((d,i) => (
                            <Bar key={i} $dur={d * 1.4 + 0.4} $del={i * 0.07} $playing={playing} />
                          ))}
                        </Visualizer>
                        <AudioControlsRow>
                          <audio ref={mediaRef} src={current.file_url} controls
                            onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)} />
                        </AudioControlsRow>
                      </>
                    )
                  ) : (
                    <PlaceholderBox>
                      <span className="ico">📻</span>
                      <p>Select a track to stream</p>
                    </PlaceholderBox>
                  )}
                </MediaArea>

                <MetaRow>
                  <div className="station-tag">{stationInfo.label}</div>
                  {current ? (
                    <>
                      <h2>{current.title}</h2>
                      {current.description && <p className="desc">{current.description}</p>}
                    </>
                  ) : (
                    <h2 style={{ color: 'rgba(255,255,255,0.25)' }}>Nothing selected</h2>
                  )}
                </MetaRow>
              </NowPlayingCard>
            </PlayerBox>
          </TwoCol>
        )}
      </Inner>
      <Footer />
    </Page>
  );
};

export default Radio;
